/**
 * LoadingState — canonical loading view.
 *
 * One component for "content is loading", with two presentation variants so a
 * single import covers both the spinner and the skeleton idioms that are today
 * re-hand-rolled as raw <CircularProgress> (188 sites) and three rival loaders.
 * Use this going forward instead of dropping a bare <CircularProgress> in a page.
 *
 * Variants (RULE-035 (c) — this is the "loading" state, surfaced two ways):
 * - spinner:  a centred <CircularProgress> with an optional label. Use for an
 *             indeterminate wait where layout shape is unknown.
 * - skeleton: `rows` shimmer <Skeleton> bars that mimic the content shape. Use
 *             when the eventual layout is a list/table-ish block (less jank).
 *
 * Sizing:
 * - `size` (small | medium | large) scales the spinner / skeleton rhythm.
 *
 * Accessibility (SPEC-FRONTEND-RULE-007 / RULE-010):
 * - The container is a polite live region (`role="status"`, `aria-busy`) so a
 *   screen reader announces the wait; it is labelled by `label` (or a default
 *   "Loading…").
 * - The spinner/skeleton graphics are decorative; the live region carries the
 *   announcement, so no extra per-graphic labelling is needed.
 */

import React from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { colors } from '../../../theme/colors';

export type LoadingStateVariant = 'spinner' | 'skeleton';
export type LoadingStateSize = 'small' | 'medium' | 'large';

export interface LoadingStateProps {
  /** Presentation: a centred spinner or shimmer skeleton rows. Defaults to `spinner`. */
  variant?: LoadingStateVariant;
  /** Scales the spinner / skeleton rhythm. Defaults to `medium`. */
  size?: LoadingStateSize;
  /** Accessible + visible label. Defaults to "Loading…". */
  label?: string;
  /** Number of skeleton rows (skeleton variant only). Defaults to 3. */
  rows?: number;
  /** Hide the visible label text (the live-region announcement is kept). */
  hideLabel?: boolean;
}

const SPINNER_SIZE: Record<LoadingStateSize, number> = {
  small: 20,
  medium: 32,
  large: 48,
};

const SKELETON_HEIGHT: Record<LoadingStateSize, number> = {
  small: 16,
  medium: 24,
  large: 36,
};

const LoadingState: React.FC<LoadingStateProps> = ({
  variant = 'spinner',
  size = 'medium',
  label,
  rows = 3,
  hideLabel = false,
}) => {
  const { _: t } = useLingui();
  const displayLabel = label || t(msg`Loading…`);

  if (variant === 'skeleton') {
    return (
      <Box role="status" aria-busy aria-label={displayLabel} sx={{ width: '100%', py: 2 }}>
        {Array.from({ length: Math.max(1, rows) }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={SKELETON_HEIGHT[size]}
            sx={{ mb: 1, bgcolor: colors.background.tertiary }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-busy
      aria-label={displayLabel}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        py: 4,
      }}
    >
      {/* eslint-disable-next-line no-restricted-syntax -- component-ok: LoadingState is the canonical wrapper around raw <CircularProgress> */}
      <CircularProgress size={SPINNER_SIZE[size]} aria-hidden />
      {!hideLabel && (
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          {displayLabel}
        </Typography>
      )}
    </Box>
  );
};

export default LoadingState;
